import { Service, service, inject } from './di'
import { ConfigService } from './ConfigService'
import { fetchJSON } from '../utils/fetch'
import { getLocaleFromUrl } from '../i18n/I18n'

@service('seoDataService')
export class SeoDataService extends Service {
    @inject configService: ConfigService
    canadianProvincesCached: any = null
    usStatesCached: any = null
    usCitiesCached: any = null
    caCitiesCached: any = null
    usMerchantsCached: any = null
    caMerchantsCached: any = null

    isCanadianState(state: string): boolean {
        if (!state) {
            return false
        }
        return this.getCaProvinces().some(
            (e: any) => e.stateCode === state.toUpperCase()
        )
    }

    getCaProvinces() {
        return this.canadianProvincesCached
    }

    getUsStates() {
        return this.usStatesCached
    }

    getTopCaProvinces() {
        const topCaProvinces = this.getTopCaCities().map((e: any) => e.state)
        return this.canadianProvincesCached.filter((e: any) =>
            topCaProvinces.includes(e.stateCode)
        )
    }

    getTopUsStates() {
        const topUsStates = this.getTopUsCities().map((e: any) => e.state)
        return this.usStatesCached.filter((e: any) =>
            topUsStates.includes(e.stateCode)
        )
    }

    getTopCities() {
        const locale = getLocaleFromUrl()
        return locale === 'en-us'
            ? this.getTopUsCities()
            : this.getTopCaCities()
    }

    getTopCaCities() {
        return this.caCitiesCached
    }

    getTopUsCities() {
        return this.usCitiesCached
    }

    getTopCanadaMerchants() {
        return this.caMerchantsCached
    }

    getTopUsMerchants() {
        return this.usMerchantsCached
    }

    getTopMerchants(locale: string): any {
        return locale === 'en-us'
            ? [
                  'Aldi',
                  'Family Dollar',
                  'Target',
                  'Dollar General',
                  'Publix',
                  'Home Depot',
                  'Meijer',
                  'Walmart',
                  'JCPenney',
                  'Food Lion',
                  'Albertsons',
                  'CVS Pharmacy',
                  "Lowe's",
                  'Food 4 Less',
                  'Costco',
                  'Walgreens',
                  'Dollar Tree',
                  'Safeway',
                  'Kroger Houston',
                  'Big Lots',
                  'Winn-Dixie',
                  'Lidl',
              ]
            : [
                  'Walmart',
                  'No Frills',
                  'Canadian Tire',
                  'Food Basics',
                  'RCSS Click & Collect',
                  'Shoppers Drug Mart',
                  'FreshCo',
                  'Metro',
                  'Giant Tiger',
                  'Costco',
                  'Home Depot',
                  'Sobeys',
                  'Best Buy',
                  "Hudson's Bay",
                  'Staples',
                  'Loblaws Click & Collect',
                  'Home Hardware',
                  "Toys 'R' Us",
                  'The Brick',
                  'M&M Food Market',
                  "Lowe's",
                  'Princess Auto',
                  'London Drugs',
                  'JYSK',
                  "Leon's",
                  'Zehrs Click & Collect',
                  'Foodland Ontario',
                  'The Source',
                  "Fortino's",
                  "Babies 'R' Us",
                  'Rexall',
                  'Home Outfitters',
                  'Factory Direct ST',
                  'Maxi',
                  'Super C',
                  'IGA Quebec',
                  'Your Independent Grocer',
                  'IKEA',
                  'PetSmart',
                  'RONA',
              ]
    }

    async fetchCanadianProvinces() {
        await this.fetchAndCache(
            '/state?country=ca',
            'canadianProvincesCached',
            this
        )
    }

    async fetchUsStates() {
        await this.fetchAndCache('/state?country=us', 'usStatesCached', this)
    }

    async fetchUsCities() {
        await this.fetchAndCache('/city/top/en-us', 'usCitiesCached', this)
    }

    async fetchCaCities() {
        await this.fetchAndCache('/city/top/en-ca', 'caCitiesCached', this)
    }

    async fetchUsMerchants() {
        await this.fetchAndCache(
            '/merchants/top/en-us',
            'usMerchantsCached',
            this
        )
    }

    async fetchCaMerchants() {
        await this.fetchAndCache(
            '/merchants/top/en-ca',
            'caMerchantsCached',
            this
        )
    }

    private fetchAndCache = async (
        url: string,
        key: string,
        cacheObject: { [key: string]: any }
    ) => {
        if (cacheObject[key]) {
            return
        }

        const response = await fetchJSON(`${this.getRootUrl()}${url}`)
        cacheObject[key] = response
    }

    private getRootUrl() {
        return this.configService.FLIPP_COM_APIS
    }
}
